import { api } from "@/lib/api/api";

const FALLBACK_MESSAGE = "Sorry we couldn't save the changes";

const CALENDAR_ERROR_MESSAGES: Record<string, string> = {
  cannot_change_reference_id:
    "The connected room can't be changed after the calendar is created.",
  cannot_change_type:
    "The calendar type can't be changed after the calendar is created.",
  cannot_change_source:
    "The calendar provider can't be changed after the calendar is created.",
  calendar_user_invalid:
    "The resource email is invalid. Check the email and try again.",
  integration_already_exists:
    "Another calendar is already connected to this email.",
  reference_already_exists:
    "This room already has a calendar configured."
};

export async function getCalendarErrorMessage(
  error: unknown
): Promise<string> {
  const code = extractErrorCode(error);
  if (!code) return FALLBACK_MESSAGE;

  if (code === "integration_already_exists") {
    const referenceId = extractReferenceId(error);
    if (referenceId) {
      const roomName = await fetchResourceName(referenceId);
      if (roomName) {
        return `Another calendar is already connected to this email on "${roomName}".`;
      }
    }
  }

  return CALENDAR_ERROR_MESSAGES[code] ?? FALLBACK_MESSAGE;
}

type CalendarErrorPayload = {
  response?: {
    result?: {
      error?: {
        code?: string;
        details?: { reference_id?: string };
      };
    };
  };
};

function extractErrorCode(error: unknown): string | null {
  const code = (error as CalendarErrorPayload)?.response?.result?.error?.code;
  return typeof code === "string" ? code : null;
}

function extractReferenceId(error: unknown): string | null {
  const referenceId = (error as CalendarErrorPayload)?.response?.result?.error
    ?.details?.reference_id;
  return typeof referenceId === "string" && referenceId.length > 0
    ? referenceId
    : null;
}

async function fetchResourceName(resourceId: string): Promise<string | null> {
  try {
    const res = await api.client.organizations.resourceGet(resourceId);
    if (!res.ok || !res.result?.resource) return null;
    return res.result.resource.name;
  } catch {
    return null;
  }
}
